import { useParams } from '@tanstack/react-router';
import { FileText, Network, Wrench } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type CitationFocus, DocumentViewerContent } from '@/components/case-partner/DocumentViewerContent';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import { type SidePanelTab, useSidePanel } from '@/components/contexts/SidePanelContext';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { DeepAnalysisDocumentDialog } from '@/components/deep-analysis/DeepAnalysisDocumentDialog';
import { DeepAnalysisHeaderControl } from '@/components/deep-analysis/DeepAnalysisHeaderControl';
import { DetailNavigator } from '@/components/knowledge-graph/DetailPanel';
import {
  ENTITY_CATEGORIES,
  EntitiesPanel,
  type EntityCardDetail,
  type EntityCategoryKey,
  entityCategoryCounts,
} from '@/components/knowledge-graph/EntitiesPanel';
import { Loading } from '@/components/shared';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import { Button } from '@/components/ui/NewButton';
import { useChronologies } from '@/hooks/useChronologies';
import { useChronology } from '@/hooks/useChronology';
import { useDeepAnalysisAvailability } from '@/hooks/useDeepAnalysisAvailability';
import { DOCUMENT_ENRICHMENT_SECTIONS, useDocuments } from '@/hooks/useDocuments';
import { type CanonicalEntity, useEntities } from '@/hooks/useEntities';
import { usePipelineProgress } from '@/hooks/usePipelineProgress';
import { useSuperAdminCheck } from '@/hooks/useSuperAdminCheck';

const Entities = () => {
  const { selectedCase } = useCaseContext();
  const { slug } = useParams({ from: '/matter/$slug/entities' });
  const { openPanel, closePanel, openDocumentPanel, tabs, setTabs, setActiveTab, isOpen } = useSidePanel();
  const closePanelRef = useRef(closePanel);
  closePanelRef.current = closePanel;
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [entityTab, setEntityTab] = useState<EntityCategoryKey>('parties');
  const matterGuid = slug as string | undefined;
  const { isSuperAdmin } = useSuperAdminCheck();
  const {
    isEnabled: deepAnalysisEnabled,
    isLoading: deepAnalysisAvailabilityLoading,
  } = useDeepAnalysisAvailability(matterGuid);
  const deepAnalysisMatterGuid = deepAnalysisEnabled ? matterGuid : undefined;

  const { entities, isLoading, isError, mutate: mutateEntities } = useEntities(deepAnalysisMatterGuid);
  const { events } = useChronology(deepAnalysisMatterGuid);
  const { chronologies } = useChronologies(deepAnalysisMatterGuid);
  // Only source names are read; this matches Case Partner's key so the two share one fetch.
  const { documents } = useDocuments(deepAnalysisMatterGuid, undefined, undefined, undefined, {}, getTimezone(), {
    excludeSections: DOCUMENT_ENRICHMENT_SECTIONS,
  });
  const {
    progress,
    isLoading: pipelineProgressLoading,
    mutate: mutatePipelineProgress,
  } = usePipelineProgress(deepAnalysisMatterGuid);

  const sourceNameByDocumentGuid = useMemo(() => {
    const m = new Map<string, string>();
    for (const doc of documents) {
      if (!doc.guid || m.has(doc.guid)) continue;
      const name = doc.displayName || doc.fileName;
      if (name) m.set(doc.guid, name);
    }
    return m;
  }, [documents]);

  const headerActions = useMemo(() => {
    if (!matterGuid || !deepAnalysisEnabled) return null;

    const deepAnalysisControl = (
      <DeepAnalysisHeaderControl
        currentDocuments={progress?.scope?.current}
        totalDocuments={progress?.scope?.total}
        removingDocuments={progress?.scope?.removing}
        isLoading={pipelineProgressLoading}
        onClick={() => setAnalysisDialogOpen(true)}
      />
    );

    return (
      <div className="flex items-center gap-2">
        {isSuperAdmin && (
          <Button
            size="sm"
            variant="outline"
            icon={Wrench}
            text="Edit entities"
            tooltip="Super admin: fix roles, aliases, sides and descriptions in a separate window"
            onClick={() => window.open(
              `/matter/${matterGuid}/entity-admin`,
              'crimson-entity-admin',
              'noopener,noreferrer,width=900,height=1000',
            )}
          />
        )}
        {deepAnalysisControl}
      </div>
    );
  }, [deepAnalysisEnabled, isSuperAdmin, matterGuid, pipelineProgressLoading, progress]);

  useShellHeader({
    header: 'Entities',
    headerActions,
  });

  const categoryCounts = useMemo(() => entityCategoryCounts(entities), [entities]);
  const visibleCategories = useMemo(
    () => ENTITY_CATEGORIES.filter(category => (categoryCounts.get(category.key) ?? 0) > 0),
    [categoryCounts],
  );
  const activeCategory = visibleCategories.some(category => category.key === entityTab)
    ? entityTab
    : (visibleCategories[0]?.key ?? 'parties');
  const headerTabs = useMemo(
    () => (
      visibleCategories.length === 0 ? null : (
        <HeaderTabs
          items={visibleCategories.map(category => ({
            value: category.key,
            label: `${category.label} (${categoryCounts.get(category.key) ?? 0})`,
          }))}
          value={activeCategory}
          onChange={(value) => {
            const next = value as EntityCategoryKey;
            if (next === activeCategory) return;
            setEntityTab(next);
            closePanelRef.current();
          }}
          ariaLabel="Entities"
          idBase="entities-category"
        />
      )
    ),
    [activeCategory, categoryCounts, visibleCategories],
  );
  useShellHeaderBelow(typeof selectedCase === 'undefined' ? null : headerTabs);

  const handleDeepAnalysisChanged = useCallback(async () => {
    await Promise.all([
      mutateEntities(),
      mutatePipelineProgress(),
    ]);
  }, [mutateEntities, mutatePipelineProgress]);

  const colorByCategory = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const chronology of chronologies) {
      for (const group of chronology.tag_groups || []) {
        m.set(group.label, group.color);
        m.set(group.group_key, group.color);
      }
    }
    return m;
  }, [chronologies]);

  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  const previousCategoryRef = useRef(activeCategory);
  useEffect(() => {
    if (previousCategoryRef.current === activeCategory) return;
    previousCategoryRef.current = activeCategory;
    closePanelRef.current();
  }, [activeCategory]);

  const handleOpenDocument = useCallback((documentGuid: string, segmentIndex?: number) => {
    if (!matterGuid) return;
    openDocumentPanel({
      documentGuid,
      matterGuid,
      segmentIndex,
      activeTab: 'document',
    });
  }, [matterGuid, openDocumentPanel]);

  const handleDocumentClick = useCallback((documentGuid: string, focus?: CitationFocus) => {
    const docTab: SidePanelTab = {
      id: 'document',
      label: 'Document',
      icon: FileText,
      content: (
        <DocumentViewerContent
          key={`${documentGuid}-${focus?.canonicalId ?? ''}`}
          documentGuid={documentGuid}
          matterGuid={matterGuid}
          focusCitation={focus}
        />
      ),
    };
    const currentTabs = tabsRef.current;
    const existing = currentTabs.findIndex(tab => tab.id === 'document');
    if (existing >= 0) {
      const updated = [...currentTabs];
      updated[existing] = docTab;
      setTabs(updated);
    } else {
      setTabs([...currentTabs, docTab]);
    }
    setActiveTab('document');
  }, [matterGuid, setActiveTab, setTabs]);

  const handleEntityChanged = useCallback(() => {
    void mutateEntities();
    closePanelRef.current();
  }, [mutateEntities]);

  const handleOpenSidePanel = useCallback((entity: CanonicalEntity, view: EntityCardDetail | null) => {
    const detailsTabId = 'case-theory';
    openPanel([
      {
        id: detailsTabId,
        label: 'Details',
        icon: Network,
        content: (
          <DetailNavigator
            key={`${entity.id}-${view ?? 'entity'}`}
            item={{ kind: 'entity', entity }}
            initialView={view ?? undefined}
            events={events}
            entities={entities}
            onDocumentClick={handleDocumentClick}
            matterGuid={matterGuid}
            onEntityChanged={handleEntityChanged}
            colorByCategory={colorByCategory}
            sourceNameByDocumentGuid={sourceNameByDocumentGuid}
            onPassageClick={handleOpenDocument}
          />
        ),
      },
    ], detailsTabId);
  }, [
    colorByCategory,
    entities,
    events,
    handleDocumentClick,
    handleEntityChanged,
    handleOpenDocument,
    matterGuid,
    openPanel,
    sourceNameByDocumentGuid,
  ]);

  return (
    <div className="overflow-x-hidden h-full flex flex-col">
      {typeof selectedCase === 'undefined' ? (
        <NoCaseSelected />
      ) : (
        <div className="flex-1 min-h-0">
          {deepAnalysisAvailabilityLoading ? (
            <div className="flex items-center justify-center py-20"><Loading /></div>
          ) : !deepAnalysisEnabled ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              Deep Analysis is not enabled for this matter.
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-20"><Loading /></div>
          ) : isError ? (
            <div className="flex items-center justify-center py-20 text-red-400">Failed to load entities.</div>
          ) : (
            <EntitiesPanel
              entities={entities}
              events={events}
              sourceNameByDocumentGuid={sourceNameByDocumentGuid}
              category={activeCategory}
              onOpenSidePanel={handleOpenSidePanel}
              onCloseSidePanel={() => closePanelRef.current()}
              panelOpen={isOpen}
            />
          )}
        </div>
      )}
      {matterGuid && deepAnalysisEnabled && (
        <DeepAnalysisDocumentDialog
          open={analysisDialogOpen}
          onOpenChange={setAnalysisDialogOpen}
          matterGuid={matterGuid}
          onChanged={handleDeepAnalysisChanged}
        />
      )}
    </div>
  );
};

export default Entities;
